import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  TDisableJourney,
  TAlertConfig,
  TEvTariffId,
  TPriceCap,
  TPriceCapDdSavings,
  TPromotions
} from 'api/models/TAppConfig';

import type { TAppConfigProfile } from 'config/app-config';
import type { NormalizedError } from 'errors/types';
import type { RootState } from 'state/store';

export type ConfigEntryData =
  | TDisableJourney
  | TAlertConfig
  | TEvTariffId
  | TPriceCap
  | TPriceCapDdSavings
  | TPromotions;
export type ConfigEntry = {
  data?: ConfigEntryData;
  errors?: NormalizedError[];
  isLoading?: boolean;
};

type TFlags = Record<string, boolean>;

export type SettingsState = {
  showFuelSelection: boolean;
  showChangeOfTenancy: boolean;
  isActionUpsell: boolean;
  isMixedMeterJourney: boolean;
  config: Partial<Record<TAppConfigProfile, ConfigEntry>>;
  flags: TFlags;
};

type SetIsLoadingPayload = {
  configId: string;
};

type SetSuccessPayload = {
  configId: string;
  data: ConfigEntryData;
};

type SetErrorPayload = {
  configId: string;
  errors: NormalizedError[];
};

type SetSettingsPayload = Partial<SettingsState>;

export const initialState: SettingsState = {
  showFuelSelection: true,
  showChangeOfTenancy: true,
  isActionUpsell: false,
  isMixedMeterJourney: false,
  config: {},
  flags: {}
};

export const slice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setSettings: (state, { payload }: PayloadAction<SetSettingsPayload>) => {
      return { ...state, ...payload };
    },
    setShowFuelSelection: (state, { payload }: PayloadAction<boolean>) => {
      state.showFuelSelection = payload;
    },
    setShowChangeOfTenancy: (state, { payload }: PayloadAction<boolean>) => {
      state.showChangeOfTenancy = payload;
    },
    setIsActionUpsell: (state, { payload }: PayloadAction<boolean>) => {
      state.isActionUpsell = payload;
    },
    setIsMixedMeterJourney: (state, { payload }: PayloadAction<boolean>) => {
      state.isMixedMeterJourney = payload;
    },
    setFlags: (state, { payload }: PayloadAction<TFlags>) => {
      state.flags = payload;
    },
    setIsLoading: (state, { payload }: PayloadAction<SetIsLoadingPayload>) => {
      const { configId } = payload;
      state.config[configId] = {};
      state.config[configId].isLoading = true;
      state.config[configId].errors = initialState.config[configId]?.errors;
    },
    setSuccess: (state, { payload }: PayloadAction<SetSuccessPayload>) => {
      const { configId, data } = payload;
      state.config[configId] = {};
      state.config[configId].isLoading = false;
      state.config[configId].data = data;
      state.config[configId].errors = initialState.config[configId]?.errors;
    },
    setError: (state, { payload }: PayloadAction<SetErrorPayload>) => {
      const { configId, errors } = payload;
      state.config[configId] = {};
      state.config[configId].isLoading = false;
      state.config[configId].data = initialState.config[configId]?.data;
      state.config[configId].errors = errors;
    }
  }
});

export const {
  setInitialState,
  setSettings,
  setShowFuelSelection,
  setShowChangeOfTenancy,
  setIsActionUpsell,
  setIsMixedMeterJourney,
  setFlags,
  setIsLoading,
  setSuccess,
  setError
} = slice.actions;

export const selectSlice = (state: RootState) => state[slice.name];
export const selectShowFuelSelection = (state: RootState) =>
  selectSlice(state).showFuelSelection;
export const selectShowChangeOfTenancy = (state: RootState) =>
  selectSlice(state).showChangeOfTenancy;
export const selectIsActionUpsell = (state: RootState) =>
  selectSlice(state).isActionUpsell;
export const selectIsMixedMeterJourney = (state: RootState) =>
  selectSlice(state).isMixedMeterJourney;

export const selectFlags = (state: RootState) => selectSlice(state).flags;

export const selectConfig = (state: RootState): SettingsState['config'] =>
  selectSlice(state).config;

// by id
export const selectConfigById = (
  state: RootState,
  configId: string
): ConfigEntry => selectConfig(state)[configId] || {};
export const selectDataById = (
  state: RootState,
  configId: string
): ConfigEntryData | undefined => selectConfigById(state, configId).data;
export const selectIsLoadingById = (state: RootState, configId: string) =>
  selectConfigById(state, configId).isLoading;
export const selectErrorsById = (state: RootState, configId: string) =>
  selectConfigById(state, configId).errors;

export default slice.reducer;
